import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { LatexRenderer } from '../../components/ui/LatexRenderer';
export type FractionVisualType = 'pizza' | 'bar';
interface FractionVisualProps {
  numerator: number;
  denominator: number;
  label?: string;
  type?: FractionVisualType;
  showEaten?: boolean;
  /** 'stimulus' = compact visual only (shown in-question), 'support' = educational panel (shown after answer) */
  mode?: 'stimulus' | 'support';
  /** For showing multiple fractions (e.g., addition: 1/4 + 2/4) */
  fractions?: Array<{
    numerator: number;
    denominator: number;
    label?: string;
    visualType?: FractionVisualType;
  }>;
  /** Operator to show between fractions (default: '+') */
  operator?: '+' | '-' | '×' | '÷';
}
function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}
// ─── Pizza SVG ────────────────────────────────────────────────────────────────
function PizzaSVG({
  filledSlices,
  totalSlices,
  size = 120,
  animationDelay = 0
}: {
  filledSlices: number;
  totalSlices: number;
  size?: number;
  animationDelay?: number;
}) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 6;
  const crustR = r + 4;
  const slices = Array.from({
    length: totalSlices
  }, (_, i) => {
    const startDeg = i * 360 / totalSlices - 90;
    const endDeg = (i + 1) * 360 / totalSlices - 90;
    const startRad = startDeg * Math.PI / 180;
    const endRad = endDeg * Math.PI / 180;
    const largeArc = 360 / totalSlices > 180 ? 1 : 0;
    const x1 = cx + r * Math.cos(startRad);
    const y1 = cy + r * Math.sin(startRad);
    const x2 = cx + r * Math.cos(endRad);
    const y2 = cy + r * Math.sin(endRad);
    const isFilled = i < filledSlices;
    return {
      x1,
      y1,
      x2,
      y2,
      startRad,
      endRad,
      largeArc,
      isFilled
    };
  });
  return <motion.svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`Taartdiagram: ${filledSlices} van ${totalSlices} stukken gekleurd`} initial={{
    opacity: 0,
    scale: 0.88
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    delay: animationDelay,
    type: 'spring',
    stiffness: 240,
    damping: 18
  }} className="drop-shadow-md" data-id="element-9221">
      {/* Crust ring */}
      <circle cx={cx} cy={cy} r={crustR} fill="#92400E" data-id="element-9222" />
      {/* Base disc */}
      <circle cx={cx} cy={cy} r={r} fill="#FDE68A" data-id="element-9223" />

      {/* Slices */}
      {totalSlices === 1 ?
    // Special case: whole pizza
    <circle cx={cx} cy={cy} r={r} fill="#F59E0B" data-id="element-9224" /> : slices.map((s, i) => <motion.path key={i} d={`M ${cx} ${cy} L ${s.x1} ${s.y1} A ${r} ${r} 0 ${s.largeArc} 1 ${s.x2} ${s.y2} Z`} fill={s.isFilled ? '#F59E0B' : '#E5E7EB'} stroke="white" strokeWidth={size > 100 ? 2.5 : 2} strokeLinejoin="round" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: animationDelay + i * 0.04
    }} data-id="element-9225" />)}

      {/* Divider lines from center (drawn on top for crispness) */}
      {totalSlices > 1 && slices.map((s, i) => <line key={`line-${i}`} x1={cx} y1={cy} x2={s.x1} y2={s.y1} stroke="white" strokeWidth={size > 100 ? 2.5 : 2} strokeLinecap="round" data-id="element-9226" />)}
    </motion.svg>;
}
// ─── Bar visual ───────────────────────────────────────────────────────────────
function BarSVG({
  filledSlices,
  totalSlices,
  animationDelay = 0
}: {
  filledSlices: number;
  totalSlices: number;
  animationDelay?: number;
}) {
  const width = 200;
  const height = 40;
  const sliceW = width / totalSlices;
  return <motion.svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`Staafdiagram: ${filledSlices} van ${totalSlices} delen gekleurd`} initial={{
    opacity: 0,
    y: 4
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: animationDelay,
    duration: 0.3
  }} className="drop-shadow-sm" data-id="element-9227">
      <rect x={0} y={0} width={width} height={height} rx={6} fill="#E5E7EB" data-id="element-9228" />
      {Array.from({
      length: totalSlices
    }, (_, i) => {
      const isFilled = i < filledSlices;
      const rx = i === 0 ? 6 : 0;
      const rrx = i === totalSlices - 1 ? 6 : 0;
      return isFilled ? <motion.rect key={i} x={i * sliceW} y={0} width={sliceW} height={height} fill="#F59E0B" rx={rx} initial={{
        scaleX: 0,
        originX: 0
      }} animate={{
        scaleX: 1
      }} transition={{
        delay: animationDelay + i * 0.05,
        duration: 0.25
      }} data-id="element-9229" /> : null;
    })}
      {/* Dividers */}
      {Array.from({
      length: totalSlices - 1
    }, (_, i) => <line key={`div-${i}`} x1={(i + 1) * sliceW} y1={0} x2={(i + 1) * sliceW} y2={height} stroke="white" strokeWidth={2} data-id="element-9230" />)}
      <rect x={0} y={0} width={width} height={height} rx={6} fill="none" stroke="#D1D5DB" strokeWidth={1.5} data-id="element-9231" />
    </motion.svg>;
}
// ─── Main component ───────────────────────────────────────────────────────────
export function FractionVisual({
  numerator,
  denominator,
  label,
  type = 'pizza',
  showEaten = false,
  mode = 'support',
  fractions,
  operator = '+'
}: FractionVisualProps) {
  // ── MULTI-FRACTION MODE (for addition/subtraction/multiplication) ─────────
  if (fractions && fractions.length > 0) {
    return <div className="flex flex-col items-center gap-5 py-6 px-4 sm:px-8 bg-slate-50/80 border border-slate-200 rounded-2xl shadow-sm w-full max-w-2xl mx-auto" data-id="element-9232">
        {label && <p className="text-base font-semibold text-slate-700 text-center mb-2" data-id="element-9233">
            <LatexRenderer content={label} data-id="element-9234" />
          </p>}

        <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6 w-full" data-id="element-9235">
          {fractions.map((frac, idx) => {
          const safeDen = isFiniteNumber(frac.denominator) ? Math.max(1, Math.floor(frac.denominator)) : 1;
          const safeNum = isFiniteNumber(frac.numerator) ? Math.max(0, Math.floor(frac.numerator)) : 0;
          const visualType = frac.visualType || type;
          return <Fragment key={idx} data-id="element-9236">
                {idx > 0 && <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-indigo-100 border-2 border-white shadow-sm shrink-0 z-10 -my-2 sm:my-0 sm:-mx-3" data-id="element-9237">
                    <span className="text-2xl sm:text-3xl font-black text-indigo-600 leading-none pb-0.5" data-id="element-9238">
                      {operator}
                    </span>
                  </div>}
                <div className="flex flex-col items-center gap-3 bg-white p-4 rounded-xl border border-slate-100 shadow-sm w-full sm:w-auto min-w-[220px]" data-id="element-9239">
                  {visualType === 'bar' ? <BarSVG filledSlices={safeNum} totalSlices={safeDen} animationDelay={idx * 0.1} data-id="element-9240" /> : <PizzaSVG filledSlices={safeNum} totalSlices={safeDen} size={mode === 'stimulus' ? 100 : 120} animationDelay={idx * 0.1} data-id="element-9241" />}
                  {frac.label && <span className="text-sm font-semibold text-slate-600" data-id="element-9242">
                      <LatexRenderer content={frac.label} data-id="element-9243" />
                    </span>}
                </div>
              </Fragment>;
        })}
        </div>
      </div>;
  }
  // ── SINGLE FRACTION MODE (original behavior) ──────────────────────────────
  const safeDen = isFiniteNumber(denominator) ? Math.max(1, Math.floor(denominator)) : 1;
  const safeNum = isFiniteNumber(numerator) ? Math.max(0, Math.floor(numerator)) : 0;
  const wholeItems = Math.floor(safeNum / safeDen);
  const remainder = safeNum % safeDen;
  // How many visuals to render
  const visuals: Array<{
    filled: number;
    total: number;
  }> = [...Array.from({
    length: wholeItems
  }, () => ({
    filled: safeDen,
    total: safeDen
  })), ...(remainder > 0 ? [{
    filled: remainder,
    total: safeDen
  }] : []), ...(safeNum === 0 ? [{
    filled: 0,
    total: safeDen
  }] : [])];
  // ── STIMULUS MODE: clean visual only, no explanations ────────────────────
  if (mode === 'stimulus') {
    return <div className="flex flex-col items-center gap-3 py-2" data-id="element-9244">
        {label && <p className="text-xs font-semibold text-slate-500 text-center" data-id="element-9245">
            <LatexRenderer content={label} data-id="element-9246" />
          </p>}
        <div className="flex flex-wrap justify-center items-center gap-3" data-id="element-9247">
          {visuals.map((v, i) => type === 'bar' ? <BarSVG key={i} filledSlices={v.filled} totalSlices={v.total} animationDelay={i * 0.08} data-id="element-9248" /> : <PizzaSVG key={i} filledSlices={v.filled} totalSlices={v.total} size={100} animationDelay={i * 0.08} data-id="element-9249" />)}
        </div>
      </div>;
  }
  // ── SUPPORT MODE: visual + clean fraction label ───────────────────────────
  return <div className="flex flex-col items-center gap-4 py-3" data-id="element-9250">
      {label && <p className="text-sm font-semibold text-slate-600 text-center" data-id="element-9251">
          <LatexRenderer content={label} data-id="element-9252" />
        </p>}

      {/* Visuals */}
      <div className="flex flex-wrap justify-center items-center gap-4" data-id="element-9253">
        {visuals.map((v, i) => type === 'bar' ? <BarSVG key={i} filledSlices={v.filled} totalSlices={v.total} animationDelay={i * 0.1} data-id="element-9254" /> : <PizzaSVG key={i} filledSlices={v.filled} totalSlices={v.total} size={140} animationDelay={i * 0.1} data-id="element-9255" />)}
      </div>

      {/* Fraction label */}
      <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 shadow-sm" data-id="element-9256">
        {/* Fraction notation */}
        <div className="flex flex-col items-center leading-none" data-id="element-9257">
          <span className="text-2xl font-black text-amber-700" data-id="element-9258">{safeNum}</span>
          <div className="w-8 h-0.5 bg-amber-400 my-1.5 rounded-full" data-id="element-9259" />
          <span className="text-2xl font-black text-slate-700" data-id="element-9260">{safeDen}</span>
        </div>
        <div className="w-px h-10 bg-amber-200" data-id="element-9261" />
        {/* Description */}
        <p className="text-sm font-medium text-slate-700 leading-snug max-w-[160px]" data-id="element-9262">
          {wholeItems > 0 && remainder > 0 ? <>
              <span className="text-emerald-600 font-bold" data-id="element-9263">{wholeItems}</span>{' '}
              heel
              {wholeItems > 1 ? 'e' : ''} +{' '}
              <span className="text-amber-600 font-bold" data-id="element-9264">
                {remainder}/{safeDen}
              </span>
            </> : wholeItems > 0 ? <>
              <span className="text-emerald-600 font-bold" data-id="element-9265">{wholeItems}</span>{' '}
              hele pizza{wholeItems > 1 ? "'s" : ''}
            </> : <>
              <span className="text-amber-600 font-bold" data-id="element-9266">{safeNum}</span> van de{' '}
              <span className="font-bold" data-id="element-9267">{safeDen}</span> stukken
            </>}
        </p>
      </div>
    </div>;
}